import { Box, Typography, useTheme } from '@mui/material';
import { ProductAllowanceUsage } from '@app/types/payment/subscription';
import { CaseUsageInput } from '@utils/case-quota';
import { allowanceNoun } from '@shared/utils/plan-catalogue';

interface DocumentsUsageProps {
  /**
   * Remaining allowance per product, from the usage endpoint. Plans are sold
   * per product now, so there is one bar per product rather than a single
   * case counter.
   */
  allowances?: ProductAllowanceUsage[];
  /** True when the account has an active plan of any kind. */
  hasSubscription: boolean;
  /**
   * The account-wide case counter from the usage endpoint. The backend still
   * enforces it on case creation (check_case_creation_limit) whenever
   * cases_limit is above 0, so it must be shown; 0 means no case cap.
   */
  caseUsage?: CaseUsageInput | null;
}

/** cases_limit 0 is how the backend stores "no case cap" (services.py). */
const NO_CASE_CAP = 0;

/** A finite, non-negative count from the payload, or null when unknown. */
function parseCount(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.max(0, value);
}

function UsageBar({ used, limit }: { used: number; limit: number }) {
  const theme = useTheme();
  const progressPercentage =
    limit > 0 ? Math.min(100, (100 * used) / limit) : 0;
  const exhausted = limit > 0 && used >= limit;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: 12,
        borderRadius: 1,
        border: 1,
        borderColor: theme.palette.primary.main,
        bgcolor: theme.palette.primary.light,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: `${progressPercentage}%`,
          backgroundColor: exhausted
            ? theme.palette.error.main
            : theme.palette.primary.main,
          transition: 'width 0.3s ease-in',
        }}
      />
    </Box>
  );
}

function DocumentsUsage({
  allowances,
  hasSubscription,
  caseUsage,
}: DocumentsUsageProps) {
  const theme = useTheme();
  // A missing or non-numeric limit is unknown, not unlimited: falling back to
  // 0 here would restore "Unlimited usage" next to an enforced cap, the exact
  // defect of ZLAYT-1422, as a silent default.
  const caseLimit = parseCount(caseUsage?.cases_limit);
  const casesUsed = parseCount(caseUsage?.cases_created) ?? 0;
  const hasAllowances = !!allowances && allowances.length > 0;

  if (!hasSubscription) {
    return (
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No active subscription - choose a plan to get started
        </Typography>
        <Box
          sx={{
            width: '100%',
            height: 12,
            borderRadius: 1,
            border: 1,
            borderColor: theme.palette.grey[400],
            bgcolor: theme.palette.grey[100],
          }}
        />
      </Box>
    );
  }

  // Unlimited only when nothing is capped: no metered product and no case
  // limit. Saying "Unlimited" next to a 3-case limit that the backend enforces
  // was the contradiction in ZLAYT-1422.
  if (!hasAllowances && caseLimit === NO_CASE_CAP) {
    return (
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Unlimited usage on your current plan
        </Typography>
      </Box>
    );
  }

  if (!hasAllowances && caseLimit === null) {
    return (
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Usage details are unavailable right now
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {caseLimit !== null && caseLimit > NO_CASE_CAP && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {/* "created", not "used": the backend counts every case created
                this period, deleted ones included, so the list can show fewer
                (same wording as the create-case notice). */}
            {`${casesUsed} of ${caseLimit} ${
              caseLimit === 1 ? 'case' : 'cases'
            } created this billing period`}
          </Typography>
          <UsageBar used={casesUsed} limit={caseLimit} />
        </Box>
      )}
      {(allowances || []).map((a) => {
        const noun = allowanceNoun(a.group, a.unit);
        // The count can run past the allowance when an admin lowers it after
        // some was used; never show "4 of 3" (ZLAYT-1626).
        const shownUsed = a.limit > 0 ? Math.min(a.used, a.limit) : a.used;
        const allUsed = a.limit > 0 && a.used >= a.limit;
        return (
          <Box key={a.group}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {`${a.group}: ${shownUsed} of ${a.limit} ${noun} used this month${
                allUsed ? ' (allowance used)' : ''
              }`}
            </Typography>
            <UsageBar used={shownUsed} limit={a.limit} />
          </Box>
        );
      })}
    </Box>
  );
}

export default DocumentsUsage;
